import { io, Socket } from 'socket.io-client';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { store } from '../store/store';
import { queryClient } from '../queryClient';
import {
  addAlert,
  addVehiclePosition,
  setChannelInfo,
  setConnectionStatus,
  updateVehicleStatus,
} from '../store/slices/websocketSlice';
import { VehiclePosition, WebSocketMessage } from '../types/websocket';

/**
 * WebSocket Service Configuration
 * Centralized configuration for connection parameters
 */
interface WebSocketConfig {
  transports: string[];
  timeout: number;
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  reconnectionDelayMax: number;
  randomizationFactor: number;
}

/**
 * WebSocket Service
 * Handles real-time communication with the backend server
 * Implements singleton pattern for global state management
 * 
 * @class WebSocketService
 * @description Manages Socket.IO connections, subscriptions, and event handling
 */
class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly baseReconnectDelay = 1000;
  private subscribedChannels: Set<string> = new Set();
  private connectionPromise: Promise<void> | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_INTERVAL = 30000; // 30 seconds
  private missedHeartbeats = 0;
  private readonly MAX_MISSED_HEARTBEATS = 3;

  /**
   * Get default configuration for Socket.IO connection
   * @private
   */
  private getDefaultConfig(): WebSocketConfig {
    return {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.baseReconnectDelay,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    };
  }

  /**
   * Fetch a dedicated short-lived JWT for the websocket service.
   * Issued by the API at GET /websocket/token (requires an active session).
   *
   * @private
   * @returns {Promise<string | null>} JWT string, or null when unavailable
   */
  private async fetchWebsocketToken(): Promise<string | null> {
    // No point calling the API without a session token
    const sessionToken =
      localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!sessionToken) {
      return null;
    }

    try {
      const { data } = await axios.get<{ token: string; expires_in: number }>(
        '/websocket/token'
      );
      return data?.token || null;
    } catch (error) {
      console.error('[WebSocket] Failed to fetch websocket token:', error);
      return null;
    }
  }

  /**
   * Connect to WebSocket server
   * Implements promise-based connection with exponential backoff
   * 
   * @param {string} url - WebSocket server URL
   * @returns {Promise<void>}
   * @throws {Error} When connection fails after max attempts
   */
  async connect(url: string): Promise<void> {
    // Prevent multiple simultaneous connection attempts
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Already connected
    if (this.socket?.connected) {
      console.info('[WebSocket] Already connected');
      return Promise.resolve();
    }

    // The websocket service authenticates with a dedicated short-lived JWT.
    // The API session token stored in localStorage is an opaque string and
    // will ALWAYS be rejected by the websocket server, so it must not be sent.
    const wsToken = await this.fetchWebsocketToken();
    if (!wsToken) {
      const error = new Error(
        'Could not obtain a WebSocket auth token (user not logged in or API unreachable)'
      );
      console.warn('[WebSocket]', error.message);
      throw error;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        // Cleanup existing connection if any
        if (this.socket) {
          this.cleanupSocket();
        }

        const config = this.getDefaultConfig();

        // FIX CRIT-4: Send the dedicated WebSocket JWT for authentication.
        // The callback form re-fetches a fresh token on every reconnection
        // attempt, so auto-reconnect keeps working after the 1h token expiry.
        let firstAttempt = true;
        this.socket = io(url, {
          ...config,
          auth: (cb) => {
            if (firstAttempt) {
              firstAttempt = false;
              cb({ token: wsToken });
              return;
            }
            this.fetchWebsocketToken().then((token) =>
              cb({ token: token || '' })
            );
          },
        });

        this.setupEventListeners();
        this.startHeartbeat();

        // Timeout handler
        const connectionTimeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
          this.connectionPromise = null;
        }, config.timeout);

        // Success handler
        this.socket.once('connected', (data) => {
          clearTimeout(connectionTimeout);
          console.info('[WebSocket] Successfully connected:', data);
          
          store.dispatch(
            setConnectionStatus({
              isConnected: true,
              clientId: data.clientId,
              error: null,
              reconnectAttempts: 0,
            })
          );

          this.reconnectAttempts = 0;
          this.missedHeartbeats = 0;
          this.connectionPromise = null;
          resolve();
        });

        // Error handler
        this.socket.once('connect_error', (error) => {
          clearTimeout(connectionTimeout);
          console.error('[WebSocket] Connection error:', error.message);
          
          store.dispatch(
            setConnectionStatus({
              isConnected: false,
              clientId: null,
              error: error.message,
              reconnectAttempts: this.reconnectAttempts,
            })
          );

          this.connectionPromise = null;
          reject(error);
        });

        // Server-side auth rejection: the gateway accepts the socket, emits
        // 'error' ({ message }) and disconnects. Without this handler the
        // promise would silently wait for the 10s 'Connection timeout'.
        this.socket.once('error', (payload: { message?: string }) => {
          // Only fatal while the connection attempt is still pending.
          // After a successful connect, later 'error' emits (e.g. channel
          // authorisation denials) must NOT tear down the socket.
          if (!this.connectionPromise) {
            return;
          }

          const message = payload?.message || 'Server error';
          if (/token|authentication/i.test(message)) {
            clearTimeout(connectionTimeout);
            console.error('[WebSocket] Authentication rejected:', message);

            store.dispatch(
              setConnectionStatus({
                isConnected: false,
                clientId: null,
                error: message,
                reconnectAttempts: this.reconnectAttempts,
              })
            );

            this.connectionPromise = null;
            this.socket?.disconnect();
            reject(new Error(message));
          }
        });
      } catch (error) {
        console.error('[WebSocket] Failed to initialize connection:', error);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  /**
   * Setup all Socket.IO event listeners
   * Organized by event category for maintainability
   * 
   * @private
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.setupConnectionEvents();
    this.setupVehicleEvents();
    this.setupChannelEvents();
    this.setupErrorEvents();
    this.setupHeartbeatEvents();
  }

  /**
   * Setup connection lifecycle events
   * @private
   */
  private setupConnectionEvents(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.info('[WebSocket] Socket connected');
      
      store.dispatch(
        setConnectionStatus({
          isConnected: true,
          clientId: this.socket?.id || null,
          error: null,
          reconnectAttempts: 0,
        })
      );

      this.reconnectAttempts = 0;
      this.missedHeartbeats = 0;
      this.resubscribeToChannels();
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('[WebSocket] Disconnected:', reason);
      
      store.dispatch(
        setConnectionStatus({
          isConnected: false,
          clientId: null,
          error: `Disconnected: ${reason}`,
          reconnectAttempts: this.reconnectAttempts,
        })
      );

      this.stopHeartbeat();
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.info('[WebSocket] Reconnected after', attemptNumber, 'attempts');
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      this.reconnectAttempts = attemptNumber;
      console.info('[WebSocket] Reconnection attempt', attemptNumber);
      
      store.dispatch(
        setConnectionStatus({
          isConnected: false,
          clientId: null,
          error: `Reconnecting... (attempt ${attemptNumber}/${this.maxReconnectAttempts})`,
          reconnectAttempts: attemptNumber,
        })
      );
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[WebSocket] Reconnection failed after max attempts');
      
      store.dispatch(
        setConnectionStatus({
          isConnected: false,
          clientId: null,
          error: 'Connection failed. Please refresh the page.',
          reconnectAttempts: this.maxReconnectAttempts,
        })
      );
    });
  }

  /**
   * Setup vehicle-related events
   * Handles position updates and status changes
   * 
   * @private
   */
  private setupVehicleEvents(): void {
    if (!this.socket) return;

    // Single vehicle position update
    this.socket.on('vehicle.position.updated', this.handleVehiclePositionUpdate);
    this.socket.on('position.updated', this.handleVehiclePositionUpdate);

    // Batch vehicle positions update
    this.socket.on('vehicles.positions.updated', this.handleBatchVehiclePositions);

    // Vehicle status updates
    this.socket.on('vehicle.status.updated', this.handleVehicleStatusUpdate);
    this.socket.on('status.updated', this.handleVehicleStatusUpdate);

    // Alerts
    this.socket.on('alert.received', this.handleAlert);
  }

  /**
   * Setup channel subscription events
   * @private
   */
  private setupChannelEvents(): void {
    if (!this.socket) return;

    this.socket.on('subscribed', (data: { channel: string; clients_count: number; timestamp: string }) => {
      console.info('[WebSocket] Subscribed to channel:', data.channel);
      this.subscribedChannels.add(data.channel);
      
      store.dispatch(
        setChannelInfo({
          channel: data.channel,
          isSubscribed: true,
          clientsCount: data.clients_count,
        })
      );
    });

    this.socket.on('unsubscribed', (data: { channel: string; timestamp: string }) => {
      console.info('[WebSocket] Unsubscribed from channel:', data.channel);
      this.subscribedChannels.delete(data.channel);
      
      store.dispatch(
        setChannelInfo({
          channel: data.channel,
          isSubscribed: false,
          clientsCount: 0,
        })
      );
    });

    this.socket.on(
      'channel_info',
      (data: { channel: string; clients_count: number; is_subscribed: boolean }) => {
        store.dispatch(
          setChannelInfo({
            channel: data.channel,
            isSubscribed: data.is_subscribed,
            clientsCount: data.clients_count,
          })
        );
      }
    );
  }

  /**
   * Setup error handling events
   * @private
   */
  private setupErrorEvents(): void {
    if (!this.socket) return;

    this.socket.on('error', (error: { message: string }) => {
      console.error('[WebSocket] Server error:', error.message);
      
      store.dispatch(
        setConnectionStatus({
          isConnected: this.socket?.connected || false,
          clientId: this.socket?.id || null,
          error: error.message,
          reconnectAttempts: this.reconnectAttempts,
        })
      );
    });

    this.socket.on('connect_timeout', () => {
      console.error('[WebSocket] Connection timeout');
    });
  }

  /**
   * Setup heartbeat/ping-pong mechanism
   * @private
   */
  private setupHeartbeatEvents(): void {
    if (!this.socket) return;

    this.socket.on('pong', (data: { timestamp: string; server_time: number }) => {
      this.missedHeartbeats = 0;
      console.debug('[WebSocket] Heartbeat received:', data);
    });
  }

  /**
   * Start heartbeat interval to monitor connection health
   * @private
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.missedHeartbeats++;

        if (this.missedHeartbeats >= this.MAX_MISSED_HEARTBEATS) {
          console.warn('[WebSocket] Connection appears dead, forcing reconnection');
          this.socket.disconnect();
          this.socket.connect();
          this.missedHeartbeats = 0;
        } else {
          this.ping();
        }
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Stop heartbeat interval
   * @private
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Event handlers with proper typing
   */
  private handleVehiclePositionUpdate = (data: VehiclePosition & WebSocketMessage): void => {
    if (this.isValidVehiclePosition(data)) {
      store.dispatch(addVehiclePosition(data));
    } else {
      console.warn('[WebSocket] Invalid vehicle position data:', data);
    }
  };

  private handleBatchVehiclePositions = (
    data: { vehicles: VehiclePosition[]; company_id: number; count: number } & WebSocketMessage
  ): void => {
    if (Array.isArray(data.vehicles)) {
      data.vehicles.forEach((position) => {
        if (this.isValidVehiclePosition(position)) {
          store.dispatch(addVehiclePosition(position));
        }
      });
      console.info('[WebSocket] Batch update:', data.count, 'vehicles');
    }
  };

  private handleVehicleStatusUpdate = (data: any): void => {
    if (data.vin) {
      store.dispatch(updateVehicleStatus(data));
    }
  };

  private handleAlert = (data: any): void => {
    store.dispatch(addAlert(data));
    console.info('[WebSocket] Alert received:', data);

    // Real-time notifications: refresh the notification list so upcoming
    // interventions and new event notifications appear without a reload.
    if (data?.type === 'notification') {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['notifications'] });

      const label = data?.name
        ? `🔔 ${data.name}`
        : '🔔 Nouvelle notification';
      toast(label, { duration: 5000 });
    }
  };

  /**
   * Validate vehicle position data
   * @private
   */
  private isValidVehiclePosition(data: any): boolean {
    return (
      data &&
      typeof data.vin === 'string' &&
      typeof data.latitude === 'number' &&
      typeof data.longitude === 'number' &&
      data.latitude !== 0 &&
      data.longitude !== 0 &&
      Math.abs(data.latitude) <= 90 &&
      Math.abs(data.longitude) <= 180
    );
  }

  /**
   * Resubscribe to all channels after reconnection
   * @private
   */
  private resubscribeToChannels(): void {
    if (this.subscribedChannels.size > 0) {
      console.info('[WebSocket] Resubscribing to channels:', Array.from(this.subscribedChannels));
      
      this.subscribedChannels.forEach((channel) => {
        this.subscribeToChannel(channel);
      });
    }
  }

  /**
   * Subscribe to a specific channel
   * 
   * @param {string} channel - Channel name to subscribe to
   */
  subscribeToChannel(channel: string): void {
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot subscribe: not connected');
      return;
    }

    if (this.subscribedChannels.has(channel)) {
      console.debug('[WebSocket] Already subscribed to channel:', channel);
      return;
    }

    console.info('[WebSocket] Subscribing to channel:', channel);
    this.socket.emit('subscribe', { channel });
  }

  /**
   * Unsubscribe from a specific channel
   * 
   * @param {string} channel - Channel name to unsubscribe from
   */
  unsubscribeFromChannel(channel: string): void {
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot unsubscribe: not connected');
      return;
    }

    console.info('[WebSocket] Unsubscribing from channel:', channel);
    this.socket.emit('unsubscribe', { channel });
    this.subscribedChannels.delete(channel);
  }

  /**
   * Get channel information
   * 
   * @param {string} channel - Channel name
   */
  getChannelInfo(channel: string): void {
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot get channel info: not connected');
      return;
    }

    this.socket.emit('get_channel_info', { channel });
  }

  /**
   * Send ping to server for connection health check
   */
  ping(): void {
    if (this.socket?.connected) {
      this.socket.emit('ping');
    }
  }

  /**
   * Disconnect from WebSocket server
   * Cleans up all resources and subscriptions
   */
  disconnect(): void {
    if (this.socket) {
      console.info('[WebSocket] Disconnecting...');
      
      this.stopHeartbeat();
      this.cleanupSocket();
      this.subscribedChannels.clear();
      this.socket = null;
      this.connectionPromise = null;

      store.dispatch(
        setConnectionStatus({
          isConnected: false,
          clientId: null,
          error: null,
          reconnectAttempts: 0,
        })
      );
    }
  }

  /**
   * Cleanup socket event listeners
   * @private
   */
  private cleanupSocket(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
  }

  /**
   * Check if currently connected
   * 
   * @returns {boolean}
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get list of currently subscribed channels
   * 
   * @returns {string[]}
   */
  getSubscribedChannels(): string[] {
    return Array.from(this.subscribedChannels);
  }

  /**
   * Get current socket ID
   * 
   * @returns {string | null}
   */
  getSocketId(): string | null {
    return this.socket?.id || null;
  }

  /**
   * Get connection statistics
   * 
   * @returns {object}
   */
  getConnectionStats() {
    return {
      isConnected: this.isConnected(),
      socketId: this.getSocketId(),
      subscribedChannels: this.getSubscribedChannels(),
      reconnectAttempts: this.reconnectAttempts,
      missedHeartbeats: this.missedHeartbeats,
    };
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
